import { useMemo, useState } from 'react'
import type { Lead } from '@/types/crm'
import { useAuth } from '@/features/auth/AuthProvider'
import { createLeadFromProCompany, setProLink } from '@/features/leads/repository/leadsRepository'
import { formatDate } from '@/shared/util/dates'
import type { ProCompany } from './repository/proCompaniesRepository'
import { linkedCompanyId, orgFromVat, suggestLeads } from './proLeadMatch'

/**
 * The CRM side of a Skida PRO company, inside its row — 21 Oct 2026.
 *
 * Linked: the lead, its stage, how it was linked, and the way into the Sales
 * CRM. Not linked: the leads the evidence points at (VAT number first), any
 * lead by hand, or a new lead made from the billing profile.
 *
 * Writes go to `skidaCrmLeads` only — the CRM's own database. The product
 * company is read and never written.
 */

const METHOD: Record<string, string> = {
  vat: 'matched on VAT number',
  domain: 'matched on email domain',
  name: 'matched on company name',
  manual: 'linked by hand',
}

export function ProLeadPanel({
  company,
  linked,
  leads,
  stageLabel,
  firstStage,
}: {
  company: ProCompany
  linked: Lead | null
  leads: Lead[]
  stageLabel: (key: string) => string
  firstStage: string
}) {
  const { user } = useAuth()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [pick, setPick] = useState('')

  const suggestions = useMemo(() => (linked ? [] : suggestLeads(company, leads)), [company, leads, linked])
  const unlinked = useMemo(
    () => leads.filter((l) => !linkedCompanyId(l)).sort((a, b) => a.name.localeCompare(b.name)),
    [leads],
  )

  const run = (job: () => Promise<unknown>) => {
    setBusy(true)
    setError(null)
    job()
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setBusy(false))
  }
  const by = user?.email ?? ''

  if (linked) {
    const viaMirror = linked.skidaProCompanyId === company.id
    return (
      <div className="prolead">
        <div className="prolead__label">CRM lead</div>
        <div className="prolead__row">
          <a className="prolead__name" href={`/pro/crm?lead=${encodeURIComponent(linked.id)}`}>
            {linked.name}
          </a>
          <span className="pill">{stageLabel(linked.stage)}</span>
          <span className="micro muted">
            {viaMirror
              ? 'linked by the Skida PRO sync'
              : `${METHOD[linked.proLink?.method ?? 'manual']}${linked.proLink?.by ? ` by ${linked.proLink.by}` : ''}${linked.proLink?.at ? `, ${formatDate(linked.proLink.at)}` : ''}`}
          </span>
          {!viaMirror && (
            <button
              className="btn btn--ghost btn--tiny"
              disabled={busy}
              onClick={() => run(() => setProLink(linked.id, null))}
              title="Remove the link. The lead itself stays as it is."
            >
              Unlink
            </button>
          )}
        </div>
        {error && <p className="recform__error">{error}</p>}
      </div>
    )
  }

  const b = company.billing
  const legalName = b?.companyName || company.name
  return (
    <div className="prolead">
      <div className="prolead__label">CRM lead — not linked</div>

      {suggestions.length > 0 && (
        <div className="prolead__suggest">
          {suggestions.map((s) => (
            <div className="prolead__row" key={s.lead.id}>
              <span className="prolead__name">{s.lead.name}</span>
              <span className="pill">{stageLabel(s.lead.stage)}</span>
              <span className="micro muted">{s.why}</span>
              <button
                className="btn btn--outline btn--tiny"
                disabled={busy}
                onClick={() => run(() => setProLink(s.lead.id, { companyId: company.id, method: s.method, by }))}
              >
                Link
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="prolead__row">
        <select className="input prolead__pick" value={pick} onChange={(e) => setPick(e.target.value)} aria-label="Pick a lead to link">
          <option value="">{suggestions.length ? 'Or pick another lead…' : 'Pick a lead…'}</option>
          {unlinked.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </select>
        <button
          className="btn btn--outline btn--tiny"
          disabled={busy || !pick}
          onClick={() => run(() => setProLink(pick, { companyId: company.id, method: 'manual', by }))}
        >
          Link
        </button>
        <span className="micro muted">or</span>
        <button
          className="btn btn--primary btn--tiny"
          disabled={busy}
          title={b ? 'A new lead with the legal name, VAT number, address and email from the billing profile' : 'A new lead with the company name and email'}
          onClick={() =>
            run(() =>
              createLeadFromProCompany({
                name: legalName,
                owner: user?.displayName ?? '',
                ownerEmail: by,
                stage: firstStage,
                companyId: company.id,
                orgNumber: b ? orgFromVat(b.vatNumber) : '',
                country: b?.countryName || b?.countryCode || '',
                address: b ? [...b.addressLines, [b.postalCode, b.city].filter(Boolean).join(' ')].filter(Boolean).join(', ') : '',
                email: company.email,
              }),
            )
          }
        >
          Create lead{b ? ' from billing profile' : ''}
        </button>
      </div>
      {error && <p className="recform__error">{error}</p>}
    </div>
  )
}
